import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

/**
 * Per-deal Heartbeat push timeline. Mirrors the backend's
 * `GET /aws/heartbeat/deal/:PK/:SK/pushes` response — one row per
 * push attempt, newest first.
 *
 * Powers the "Heartbeat push history" section beneath the existing
 * Heartbeat panel on the deal page. Read-only.
 */

export type HeartbeatPushTrigger =
  | 'pandadoc_webhook'
  | 'dev_button'
  | 'no_signature_flow'
  | 'bulk_patch'
  | 'cli'
  | 'unknown'

export interface PersistedSubmissionResult {
  id: number | null
  success: boolean
  change_type?: string
  submission_type?: string
  service_line?: string
  effective_date?: string | null
  length_of_change_months?: number
  current_mrr?: number
  new_mrr?: number
  already_existed?: boolean
  error?: string
}

export interface HeartbeatPushEntity {
  PK: string
  SK: string
  EntityType: 'heartbeat_push'
  push_uuid: string
  deal_uuid: string
  deal_sk: string
  org_pk: string
  pushed_at: string
  pushed_by?: string
  trigger: HeartbeatPushTrigger
  flow: 'A' | 'B' | 'IO' | 'none'
  success: boolean
  skip_reason?: string
  /** Server-derived: a skip/failure that a later successful push for the same
   *  deal has since superseded (e.g. an org_not_linked skip fixed by a
   *  re-push). Such entries drop out of the skipped/failure buckets. */
  resolved?: boolean
  error_message?: string
  heartbeat_account_id?: number | null
  account_source?: string
  parent_account_created?: boolean
  submitter_heartbeat_user_id?: number | null
  team_members_attached?: number
  team_members_skipped_unknown_user?: number
  predecessor_source?: string
  predecessor_deal_sk?: string
  renewal_context?: {
    predecessor_deal_sk: string
    predecessor_semantic_version: string | null
    predecessor_end_date: string | null
    reason: 'predecessor_expired' | 'no_hb_contract'
  }
  emitted_submissions: number
  submission_results: PersistedSubmissionResult[]
  contract_result?: {
    id: number | null
    success: boolean
    error?: string
    start_date?: string
    end_date?: string
    /** Monthly service MRR per service line, as sent in service_budget. */
    service_budget?: Record<string, number>
  }
}

interface TimelineResponse {
  success: boolean
  pushes: HeartbeatPushEntity[]
}

export const useHeartbeatPushTimeline = (
  PK: string | undefined,
  SK: string | undefined,
): UseQueryResult<HeartbeatPushEntity[], Error> => {
  return useQuery<HeartbeatPushEntity[], Error>({
    queryKey: ['heartbeat-push-timeline', PK, SK],
    enabled: Boolean(PK && SK),
    // Timeline doesn't change between pushes — 30s stale is plenty.
    staleTime: 30_000,
    queryFn: async (): Promise<HeartbeatPushEntity[]> => {
      const res = await axios.get<TimelineResponse>(
        `${baseUrl}/aws/heartbeat/deal/${PK}/${SK}/pushes`,
      )
      return res.data.pushes ?? []
    },
  })
}
